import { useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import { Card } from "@/components/ui/Card";
import { SettingsSaveRow } from "@/pages/stammdaten/settings/SettingsSaveRow";
import {
  errorMessage,
  type SettingsSectionProps,
  type TenantSettings,
} from "@/pages/stammdaten/settings/tenantSettingsTypes";

export function CheckoutSection({ settings, canWrite, onSaved }: SettingsSectionProps) {
  const [requiresPaid, setRequiresPaid] = useState(settings.checkout?.requires_paid_invoice ?? false);
  const [departureTime, setDepartureTime] = useState(settings.checkout?.departure_time ?? "11:00");
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setRequiresPaid(settings.checkout?.requires_paid_invoice ?? false);
    setDepartureTime(settings.checkout?.departure_time ?? "11:00");
  }, [settings]);

  async function onSave(e: React.FormEvent) {
    e.preventDefault();
    if (!canWrite) return;
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      await api.patch<TenantSettings>("/api/v1/settings/checkout", {
        requires_paid_invoice: requiresPaid,
        departure_time: departureTime,
      });
      setSuccess(true);
      onSaved();
    } catch (err) {
      setError(errorMessage(err, "Check-out-Einstellungen konnten nicht gespeichert werden"));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card
      title="Abreise-Uhrzeit & Check-out"
      description="Ab wann Gäste am Abreisetag als überfällig markiert werden und ob ein Check-out bei offenen Rechnungen möglich ist."
      bodyClassName="space-y-4"
    >
      <form onSubmit={(e) => void onSave(e)} className="space-y-4">
        <fieldset className="space-y-3" disabled={!canWrite}>
          <label className="flex max-w-xs flex-col gap-1 text-sm">
            Abreise-Uhrzeit
            <input
              type="time"
              className="eh-input"
              value={departureTime}
              onChange={(e) => setDepartureTime(e.target.value)}
              disabled={!canWrite}
              required
            />
            <span className="text-xs text-label-tertiary">
              Gäste gelten ab diesem Zeitpunkt am Abreisetag als überfällig, solange sie noch
              eingecheckt sind.
            </span>
          </label>
          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              className="mt-1"
              checked={requiresPaid}
              onChange={(e) => setRequiresPaid(e.target.checked)}
              disabled={!canWrite}
            />
            <span>
              Check-out blockieren, wenn offene Rechnungen bestehen (streng). Wenn deaktiviert,
              erscheint nur eine Bestätigungswarnung.
            </span>
          </label>
        </fieldset>
        <SettingsSaveRow
          canWrite={canWrite}
          saving={saving}
          success={success}
          successText="Check-out-Einstellungen gespeichert."
          error={error}
          label="Check-out speichern"
        />
      </form>
    </Card>
  );
}
